import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import Button from '../components/Button';
import { Input } from '../components/PlayerUI';

type Step = 'email' | 'code' | 'done';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const requestCode = async (event?: FormEvent) => {
    event?.preventDefault();
    setError('');
    if (!email.trim().includes('@')) return setError(t('auth.forgot.errors.invalidEmail'));
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE}/auth/forgot-password`, { email: email.trim() });
      setInfo(response.data?.message || t('auth.forgot.defaultSent'));
      setStep('code');
    } catch (requestError: any) {
      setError(requestError?.response?.status === 429 ? t('auth.forgot.errors.tooManyRequests') : t('auth.forgot.errors.connection'));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (event?: FormEvent) => {
    event?.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) return setError(t('auth.forgot.errors.invalidOtp'));
    if (password.length < 8) return setError(t('auth.forgot.errors.weakPassword'));
    if (password !== confirmPassword) return setError(t('auth.forgot.errors.mismatch'));
    setLoading(true);
    try {
      await axios.post(`${API_BASE}/auth/reset-password`, { email: email.trim(), otp: code, newPassword: password });
      setStep('done');
    } catch (requestError: any) {
      if (requestError?.response?.status === 401) setError(t('auth.forgot.errors.invalidCode'));
      else if (requestError?.response?.status === 429) setError(t('auth.forgot.errors.tooManyAttempts'));
      else setError(t('auth.forgot.errors.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="sls-auth-page" style={{ minHeight: '100dvh', background: colors.surface, padding: 'calc(20px + env(safe-area-inset-top, 0px)) 20px 28px' }}>
      <div style={{ width: '100%', maxWidth: 420, margin: '0 auto' }}>
        <button onClick={() => navigate('/login')} style={{ minHeight: 48, padding: 0, border: 0, background: 'transparent', color: colors.accent, fontSize: 16, fontWeight: 600 }}>
          {t('auth.forgot.back')}
        </button>
        <img src="/icons/icon-192.png" alt="SL Sweep" style={{ width: 64, height: 64, marginTop: 24 }} />
        <h1 style={{ margin: '20px 0 8px', fontSize: 28, fontWeight: 700, color: colors.text }}>{step === 'done' ? t('auth.forgot.doneTitle') : t('auth.forgot.title')}</h1>
        <p style={{ margin: '0 0 28px', fontSize: 17, lineHeight: 1.5, color: colors.textSub }}>
          {step === 'email' ? t('auth.forgot.instructions') : step === 'code' ? t('auth.forgot.codeInstructions') : t('auth.forgot.passwordUpdatedSub')}
        </p>

        {error ? <div role="alert" style={{ marginBottom: 18, padding: 14, borderRadius: 12, background: colors.line, fontSize: 15, lineHeight: 1.5 }}>{error}</div> : null}
        {info && step === 'code' ? <div style={{ marginBottom: 18, padding: 14, borderRadius: 12, background: colors.line, fontSize: 15, lineHeight: 1.5 }}>{info}</div> : null}

        {step === 'email' ? (
          <form onSubmit={requestCode} style={{ display: 'grid', gap: 20 }}>
            <Input label={t('auth.forgot.emailAddress')} type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder={t('auth.forgot.emailExample')} autoComplete="email" />
            <Button label={t('auth.forgot.sendCode')} loading={loading} type="submit" />
          </form>
        ) : step === 'code' ? (
          <form onSubmit={resetPassword} style={{ display: 'grid', gap: 18 }}>
            <Input label={t('auth.forgot.otpLabel')} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} inputMode="numeric" maxLength={6} autoComplete="one-time-code" />
            <Input label={t('auth.forgot.newPassword')} type="password" value={password} onChange={event => setPassword(event.target.value)} helper={t('auth.forgot.passwordRequirement')} autoComplete="new-password" />
            <Input label={t('auth.forgot.confirmPassword')} type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} autoComplete="new-password" />
            <Button label={t('auth.forgot.resetPassword')} loading={loading} type="submit" />
            <Button label={t('auth.forgot.useDifferentEmail')} variant="secondary" onClick={() => { setStep('email'); setError(''); setInfo(''); }} />
          </form>
        ) : (
          <Button label={t('auth.forgot.backToLoginButton')} onClick={() => navigate('/login')} />
        )}

        <div style={{ marginTop: 28, fontSize: 15, lineHeight: 1.5, color: colors.textSub }}>
          {t('auth.forgot.supportPrompt')}{' '}
          <a href={`mailto:${t('auth.login.supportEmail')}`} style={{ color: colors.accent, fontWeight: 600 }}>{t('auth.forgot.contactSupport')}</a>
        </div>
      </div>
    </main>
  );
}
